import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Link, ListItemButton, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import history from 'common/history';
import { useUpdateNotification } from 'features/case/redux/updateNotification';
import { Clear, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { notificationsExport } from 'features/case/TableWrapper/utils';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';

const useStyles = makeStyles(() => ({
  notificationItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  notificationTitle: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const isAbsoluteHttpUrl = (link = '') => /^https?:\/\//i.test(link);

const openLinkInBrowser = (link) => {
  const anchor = document.createElement('a');
  anchor.href = link;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const NotificationItem = ({ notificationItem, handleClose }) => {
  const classes = useStyles();
  const { updateNotification } = useUpdateNotification();
  const [showMessage, setShowMessage] = useState(false);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const readNotification = (item) => {
    setShowMessage(!showMessage);
    if (!item.read) {
      updateNotification({ notification: { ...item, read: true } });
    }
  };

  const handleOpenNotificationClicked = (itm) => {
    // Guard: ensure a valid link exists
    if (!itm || !itm.link) return;

    const link = itm.link.trim();

    if (isAbsoluteHttpUrl(link)) {
      openLinkInBrowser(link);
      return;
    }

    // Guard: avoid navigating to a case route when no case context is available
    if (!currentCaseId) {
      return;
    }

    const basePath = history.location.pathname.replace(/\/case.*/, ``);
    const normalizedLink = link.startsWith('/') ? link : `/${link}`;
    const targetLink = normalizedLink.startsWith('/case')
      ? normalizedLink
      : `/case${normalizedLink}`;
    history.push(basePath + targetLink);
  };

  const downloadReport = async () => {
    if (notificationItem?.link && notificationItem?.type === 'batch-upload') {
      await notificationsExport(notificationItem.link);
    }
  };
  return (
    <ListItemButton
      className={!notificationItem.read && classes.notificationItemBgd}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        readNotification(notificationItem);
      }}
      disableRipple
    >
      <ListItemText
        primary={
          <div className={classes.notificationItem}>
            <div className={classes.notificationTitle}>
              <div>
                <span>{notificationItem.title}</span>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    readNotification(notificationItem);
                  }}
                  size="small"
                >
                  {showMessage ? (
                    <KeyboardArrowDown fontSize="small" />
                  ) : (
                    <KeyboardArrowRight fontSize="small" />
                  )}
                </IconButton>
              </div>
              {showMessage && (
                <Link
                  underline="hover"
                  color="inherit"
                  onClick={async (e) => {
                    if (notificationItem.type === 'batch-upload') {
                      await downloadReport();
                    } else {
                      handleOpenNotificationClicked(notificationItem);
                    }
                    handleClose(e);
                  }}
                >
                  {notificationItem.message}
                </Link>
              )}
            </div>
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                updateNotification({ notification: { ...notificationItem, hide: true } });
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </div>
        }
        secondary={moment(notificationItem.createdBy.date).fromNow()}
        primaryTypographyProps={{
          sx: {
            fontSize: '1rem',
            fontWeight: `${!notificationItem.read ? '600' : '400'}`,
          },
        }}
        secondaryTypographyProps={{
          sx: {
            pt: '0.5rem',
            fontWeight: `${!notificationItem.read ? '600' : '400'}`,
          },
        }}
      ></ListItemText>
    </ListItemButton>
  );
};

export default NotificationItem;

NotificationItem.propTypes = {
  notificationItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    link: PropTypes.string,
    type: PropTypes.string,
    read: PropTypes.bool,
    hide: PropTypes.bool,
    createdBy: PropTypes.shape({
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
  }).isRequired,
  handleClose: PropTypes.func.isRequired,
};
